/**
 * main.tsx — Chatter app entry point (D-01: distinct build from admin-app).
 *
 * Provider stack (outer→inner):
 *   QueryClientProvider → ThemeProvider → ToastProvider → ChatterGuard → WsProvider → RouterProvider
 *
 * Chatter-gated: GET /auth/me fetched once on mount; if not authenticated → /login.
 * Both chatter and admin roles may use the chatter surface (admin can observe).
 * All timestamps use Europe/Berlin (D-09) via lib/time.ts formatters.
 *
 * UI (Design Spec v0.1): the inbox + conversation are one three-column workspace
 * (ChatterWorkspace) under a persistent header + per-creator tab strip. Light/dark
 * theming follows the OS with a manual System·Light·Dark override in the header.
 *
 * CRITICAL (T-04-35 / D-13):
 *   - No aggregate revenue / KPI dashboard surface anywhere in this app.
 *   - Per-fan totalSpendForCreator is visible (D-13); no aggregate revenue shown.
 */

import * as React from 'react';
import { createRoot } from 'react-dom/client';
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet,
  useNavigate,
} from 'react-router-dom';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { ToastProvider, ThemeProvider, ThemeSwitch, useSessionKeepalive } from '@crm/ui';
import { WsProvider } from './lib/WsProvider.js';
import { authApi } from './lib/api-client.js';
import { Login } from './pages/Login.js';
import { ChatterWorkspace } from './pages/ChatterWorkspace.js';
import { AccountSettings } from './pages/AccountSettings.js';
import { AcceptInvite } from './pages/AcceptInvite.js';
import { ResetPassword } from './pages/ResetPassword.js';
import './index.css';

// ---------------------------------------------------------------------------
// TanStack Query client
// ---------------------------------------------------------------------------

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
    },
  },
});

// ---------------------------------------------------------------------------
// Auth guard — fetches /auth/me; redirects to /login if not authenticated
// ---------------------------------------------------------------------------

function ChatterGuard() {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['me'],
    queryFn: () => authApi.me(),
    retry: false,
  });

  React.useEffect(() => {
    if (!isLoading && (isError || !data)) {
      navigate('/login', { replace: true });
    }
  }, [isLoading, isError, data, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <span className="text-text-muted text-sm">Loading…</span>
      </div>
    );
  }

  if (isError || !data) return null;

  return (
    <WsProvider>
      <ChatterShell />
    </WsProvider>
  );
}

// ---------------------------------------------------------------------------
// Chatter shell — persistent header + workspace
// ---------------------------------------------------------------------------

function ChatterShell() {
  const navigate = useNavigate();
  const qc = useQuery({ queryKey: ['me'], queryFn: () => authApi.me() });

  // Sliding session: rotate the session cookie while the tab is in use so the
  // server TTL acts as an idle timeout, not a mid-shift logout.
  useSessionKeepalive(authApi.refresh);

  const handleLogout = async () => {
    await authApi.logout().catch(() => {});
    queryClient.clear();
    navigate('/login', { replace: true });
  };

  const me = qc.data;

  return (
    // h-dvh (not h-screen): lock to the VISIBLE viewport so the composer's send
    // button is never pushed under a mobile browser chrome / OS bar.
    <div className="h-dvh bg-bg text-text flex flex-col overflow-hidden">
      {/* Header */}
      <header className="border-b border-border px-4 h-12 flex items-center gap-3 bg-bg-card shrink-0">
        <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-accent text-white font-bold text-sm shrink-0">
          M
        </span>
        <span className="text-sm font-bold text-text tracking-tight">Maloum</span>
        <span className="text-[10px] font-mono font-semibold uppercase tracking-wide text-text-muted border border-border rounded px-1.5 py-0.5">
          CRM
        </span>
        <span className="text-xs text-text-subtle hidden sm:inline">Chatter workspace</span>

        <div className="ml-auto flex items-center gap-3">
          {/* Settings link — left of ThemeSwitch (UI-SPEC Screen 2 / SC-5) */}
          <button
            onClick={() => navigate('/settings')}
            className="text-xs text-text-muted hover:text-text transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 rounded"
            aria-label="Account settings"
          >
            Settings
          </button>
          <ThemeSwitch />
          {me && (
            <span
              className="text-[11px] text-text-muted font-mono border border-border rounded px-2 py-1"
              title={me.email || undefined}
            >
              {me.displayName || me.email || 'Signed in'}
            </span>
          )}
          <button
            onClick={handleLogout}
            className="text-xs text-text-muted hover:text-text border border-border rounded px-2.5 py-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60"
          >
            Sign out
          </button>
        </div>
      </header>

      {/* Workspace */}
      <main className="flex-1 min-h-0 overflow-hidden">
        <Outlet />
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Router
// ---------------------------------------------------------------------------

const router = createBrowserRouter([
  // Public routes — OUTSIDE ChatterGuard (no auth required)
  {
    path: '/login',
    element: <Login />,
  },
  {
    path: '/accept-invite',
    element: <AcceptInvite />,
  },
  {
    path: '/reset-password',
    element: <ResetPassword />,
  },
  // Authenticated routes — INSIDE ChatterGuard
  {
    path: '/',
    element: <ChatterGuard />,
    children: [
      { index: true, element: <Navigate to="/inbox" replace /> },
      // Self-service settings (D-09-13 / SC-5)
      { path: 'settings', element: <AccountSettings /> },
      // Design Spec: one three-column workspace. The creator + open conversation
      // live in the URL so the view is deep-linkable and back-button friendly.
      { path: 'inbox', element: <ChatterWorkspace /> },
      { path: 'inbox/:creatorId', element: <ChatterWorkspace /> },
      { path: 'inbox/:creatorId/c/:conversationId', element: <ChatterWorkspace /> },
    ],
  },
  {
    path: '*',
    element: <Navigate to="/login" replace />,
  },
]);

// ---------------------------------------------------------------------------
// Mount
// ---------------------------------------------------------------------------

const root = document.getElementById('root');
if (!root) throw new Error('Missing #root element');

createRoot(root).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <ToastProvider>
          <RouterProvider router={router} />
        </ToastProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);
